import { get, Writable, writable } from "svelte/store";
import { HttpPromise } from "@shared/svelte/Modules/ApiService";

import type { Distributor, DistributorEmailDomainMatch } from "@shared/types/Distributor";
import type { SalesRep } from "@shared/types/SalesRep";

const createDistributorsStore = () => {
  const { subscribe, set, update } = writable([]);

  const updateDistributor = (distributorId: number, newDistributorData: Distributor | any) => {
    return update((distributors: Distributor[]) => {
      const dIndex = distributors.findIndex(d => d.id == distributorId);

      // If distributor does not exist at Store
      if (dIndex < 0) return [...distributors, { id: distributorId, ...newDistributorData }];

      distributors[dIndex] = { ...distributors[dIndex], ...newDistributorData };
      return distributors;
    });
  };

  const updateDistributorSalesRep = (
    distributorId: number,
    salesRepId: number,
    newSalesRepData: SalesRep,
  ) => {
    return update((distributors: Distributor[]) => {
      const dIndex = distributors.findIndex(d => d.id === distributorId);
      // If distributor does not exist at Store
      if (dIndex < 0)
        return [...distributors, { id: distributorId, sales_reps: [newSalesRepData] }];

      const salesReps = distributors[dIndex].sales_reps;

      // If sales_reps array does not exist at Store
      if (!salesReps || salesReps.length === 0) {
        distributors[dIndex] = { ...distributors[dIndex], sales_reps: [newSalesRepData] };
        return distributors;
      }

      const salesRepIndex = salesReps.findIndex(rep => rep.id == salesRepId);
      distributors[dIndex].sales_reps[salesRepIndex] = {
        ...distributors[dIndex].sales_reps[salesRepIndex],
        ...newSalesRepData,
      };
      return distributors;
    });
  };

  const pushAddedSalesReps = (distributorId: number, reps: SalesRep[]) => {
    let distributors = get(DistributorsStore);
    let updatedDistributorIndex = distributors.findIndex(d => distributorId === d.id);

    distributors[updatedDistributorIndex] = {
      ...distributors[updatedDistributorIndex],
      sales_reps: reps,
    };

    set(distributors);
  };

  const distributorDomains = () => {
    const domains: DistributorEmailDomainMatch[] = [];

    get(DistributorsStore).forEach(distributor => {
      if (distributor?.email_domains?.length) domains.push(...distributor.email_domains);
    });

    return domains;
  };

  const mappedDistributors = () => {
    const distributorsMap: { [key: string]: Distributor } = {};

    get(DistributorsStore).forEach(distributor => {
      distributorsMap[distributor.id] = distributor;
    });

    return distributorsMap;
  };

  const fetchDistributors = async () => {
    let res;
    try {
      res = await HttpPromise({
        method: "GET",
        url: "/api/retailer/distributors",
        data: {
          with_cutoff_time: true,
          with_integrated: true,
          with_sales_reps: true,
          with_domains: true,
        },
      });
    } catch (e) {
      res = [];
    }
    set(res);
  };

  return {
    fetchDistributors,
    distributorDomains,
    mappedDistributors,
    pushAddedSalesReps,
    subscribe,
    set,
    update,
    updateDistributor,
    updateDistributorSalesRep,
  };
};

let DistributorsStore: Writable<Distributor[]>;

export default DistributorsStore = createDistributorsStore();
